import React, { useEffect, useMemo, useState, type FormEvent } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";

type Lane = "noaa" | "usgs";
type Candidate = {
  revisionKey: string;
  lane: Lane;
  provider: string;
  lineageKey: string;
  providerEventId: string | null;
  title: string;
  updatedAt: string | null;
};
type CandidateList = { state: string; candidates: Candidate[]; gaps?: string[] };
type SavedResponse = {
  dossier: { id: string; currentReportId: string };
  report: { id: string; title?: string };
  capture: unknown;
  manifestDigest: string;
  version: { reportId: string; sequence: number; createdAt: string; parentReportId: string | null };
};
type Failure = { message: string; retryable: boolean };

function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  throw new Error("This browser cannot create a secure request ID. Update the browser and try again.");
}

async function bodyOf(response: Response): Promise<Record<string, unknown>> {
  try {
    const body: unknown = await response.json();
    return body !== null && typeof body === "object" && !Array.isArray(body) ? body as Record<string, unknown> : {};
  } catch { return {}; }
}

function readCandidates(value: unknown): CandidateList | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const body = value as Record<string, unknown>;
  if (typeof body.state !== "string" || !Array.isArray(body.candidates)) return null;
  const candidates: Candidate[] = [];
  for (const raw of body.candidates) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const row = raw as Record<string, unknown>;
    if (typeof row.revisionKey !== "string" || (row.lane !== "noaa" && row.lane !== "usgs")
      || typeof row.provider !== "string" || typeof row.lineageKey !== "string"
      || !(row.providerEventId === null || typeof row.providerEventId === "string")
      || typeof row.title !== "string" || !(row.updatedAt === null || typeof row.updatedAt === "string")) return null;
    candidates.push({ revisionKey: row.revisionKey, lane: row.lane, provider: row.provider,
      lineageKey: row.lineageKey, providerEventId: row.providerEventId, title: row.title, updatedAt: row.updatedAt });
  }
  return { state: body.state, candidates, gaps: Array.isArray(body.gaps) ? body.gaps.filter((gap): gap is string => typeof gap === "string") : [] };
}

const EventEvidenceReviewEntry: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [facilityExposureEnabled, setFacilityExposureEnabled] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [list, setList] = useState<CandidateList | null>(null);
  const [laneFilter, setLaneFilter] = useState<"all" | Lane>("all");
  const [selectedKey, setSelectedKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<SavedResponse | null>(null);
  const [pending, setPending] = useState<{ eventRevisionKey: string; requestId: string } | null>(null);
  const [error, setError] = useState<Failure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const options = { headers: getClientIdentityHeaders(), credentials: "include" as const, cache: "no-store" as const, signal: controller.signal };
    fetch("/api/research-dossiers/events/config", options)
      .then(async (response) => response.ok ? bodyOf(response) : null)
      .then((config) => {
        if (controller.signal.aborted) return;
        setEnabled(config?.enabled === true);
        setFacilityExposureEnabled(config?.facilityExposureEnabled === true);
        setConfigLoaded(true);
        if (config?.enabled !== true) return;
        return fetch("/api/research-dossiers/events/candidates", options)
          .then(async (response) => ({ response, body: await bodyOf(response) }))
          .then(({ response, body }) => {
            if (controller.signal.aborted) return;
            const parsed = response.ok ? readCandidates(body) : null;
            if (!parsed) { setList({ state: "unavailable", candidates: [] }); return; }
            setList(parsed);
            setSelectedKey(parsed.candidates[0]?.revisionKey ?? "");
          });
      })
      .catch(() => {
        if (!controller.signal.aborted) { setConfigLoaded(true); setEnabled(false); setList({ state: "unavailable", candidates: [] }); }
      });
    return () => controller.abort();
  }, []);

  const candidates = useMemo(() => list?.candidates.filter((candidate) => laneFilter === "all" || candidate.lane === laneFilter) ?? [], [list, laneFilter]);
  const selected = candidates.find((candidate) => candidate.revisionKey === selectedKey);
  const hasUsableCandidates = list?.state === "ready" || list?.state === "truncated";

  const sendCapture = async (request: { eventRevisionKey: string; requestId: string }) => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/research-dossiers/events", {
        method: "POST", credentials: "include",
        headers: { ...getClientIdentityHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      const body = await bodyOf(response);
      if (!response.ok) {
        const detail = typeof body.detail === "string" ? `: ${body.detail}` : "";
        setError({ message: `Event review could not be saved (HTTP ${response.status})${detail}.`, retryable: response.status === 0 || response.status >= 500 });
        return;
      }
      if (!body.report || typeof body.report !== "object" || typeof (body.report as Record<string, unknown>).id !== "string"
        || !body.capture || !body.dossier || !body.version) {
        setError({ message: "The response is incomplete. The review may have been saved; retry with the same request ID.", retryable: true });
        return;
      }
      setSaved(body as unknown as SavedResponse);
      setPending(null);
    } catch {
      setError({ message: "No HTTP response was received. The save result is unknown.", retryable: true });
    } finally { setBusy(false); }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!selected || busy || (pending && error?.retryable)) return;
    try {
      const request = { eventRevisionKey: selected.revisionKey, requestId: uuid() };
      setPending(request);
      void sendCapture(request);
    } catch (cause) {
      setError({ message: cause instanceof Error ? cause.message : "Could not create a secure request ID.", retryable: false });
    }
  };

  if (!configLoaded) return <section id="event-evidence-review" className="event-evidence-review analytics-panel-section" aria-label="Retained event evidence review" aria-live="polite">Checking event review availability…</section>;

  return <section className="event-evidence-review analytics-panel-section" id="event-evidence-review" aria-labelledby="event-evidence-review-entry-title">
    <div className="analytics-command">
      <div>
        <p className="home-kicker">RETAINED EVENT EVIDENCE · DETERMINISTIC · ZERO MODEL CALLS</p>
        <h2 id="event-evidence-review-entry-title">Review evidence for one retained event</h2>
        <p className="home-summary">Which retained reports support this exact event, what changed or remains in conflict, and which missing evidence should you check next? This retrospective review is captured now. NOAA alerts and USGS events stay in separate lanes.</p>
        {facilityExposureEnabled && <p>A current acknowledged NOAA review can screen the frozen EIA-860 2025 reported plant coordinates against its retained polygon. The result names numerical screening candidates for further research; geographic exposure, ownership, current operation and loss remain unestablished.</p>}
      </div>
      {!enabled && <p role="status">New event reviews are disabled. Saved reports remain available in Reports.</p>}
      {enabled && !list && <p role="status">Loading retained NOAA alert and USGS event revisions…</p>}
      {enabled && list && !hasUsableCandidates && <div role="status">
        <p>{list.state === "empty" ? "No retained event revisions are available." : "Retained event revisions could not be read."} No review was created.</p>
        {!!list.gaps?.length && <ul>{list.gaps.map((gap) => <li key={gap}>{gap}</li>)}</ul>}
      </div>}
      {enabled && hasUsableCandidates && list?.candidates.length === 0 && <p role="status">No retained NOAA alert or USGS event revisions are available.</p>}
      {enabled && list && hasUsableCandidates && list.candidates.length > 0 && <form className="home-symbol-form analytics-form" onSubmit={submit} aria-label="Create a retained event evidence review">
        {list.state === "truncated" && <p role="status">This is a bounded candidate list ({list.candidates.length} retained {list.candidates.length === 1 ? "choice" : "choices"}). Filter by lane and select the exact retained revision to review.</p>}
        <label htmlFor="event-evidence-lane">Provider lane</label>
        <select id="event-evidence-lane" value={laneFilter} onChange={(event) => {
          const next = event.target.value as "all" | Lane;
          setLaneFilter(next);
          const nextCandidate = list.candidates.find((candidate) => next === "all" || candidate.lane === next);
          setSelectedKey(nextCandidate?.revisionKey ?? "");
        }} disabled={busy}>
          <option value="all">All lanes</option><option value="noaa">NOAA alerts</option><option value="usgs">USGS events</option>
        </select>
        <label htmlFor="event-evidence-candidate">Retained event revision</label>
        <select id="event-evidence-candidate" value={selectedKey} onChange={(event) => setSelectedKey(event.target.value)} disabled={busy || candidates.length === 0}>
          {candidates.map((candidate) => <option key={candidate.revisionKey} value={candidate.revisionKey}>
            {candidate.lane.toUpperCase()} · {candidate.title} · {candidate.providerEventId ?? candidate.lineageKey}
          </option>)}
        </select>
        {candidates.length === 0 && <p>No retained {laneFilter.toUpperCase()} revisions are in this candidate list.</p>}
        {selected && <p className="event-evidence-review-identity">Selected: {selected.provider} · {selected.providerEventId ?? "provider event ID unavailable"} · lineage {selected.lineageKey}</p>}
        <button type="submit" className="analytics-action-btn" disabled={busy || !selected || (!!pending && error?.retryable === true)}>
          {busy ? "Saving review…" : "Create saved review"}
        </button>
      </form>}
      {error && <div className="analytics-error" role="alert" aria-live="assertive">
        <p>{error.message}</p>
        {error.retryable && pending && <button type="button" className="analytics-action-btn" onClick={() => void sendCapture(pending)} disabled={busy}>Retry the same request</button>}
      </div>}
      {saved && <div aria-live="polite">
        <p>Saved review version {saved.version.sequence}. Captured now; no model or provider call was made.</p>
        <a className="analytics-action-btn" href={`/reports?id=${encodeURIComponent(saved.report.id)}`}>Open saved event review in Reports</a>
      </div>}
    </div>
  </section>;
};

export default EventEvidenceReviewEntry;
